import { parsePhoneNumberFromString, getCountries, getCountryCallingCode, AsYouType, CountryCode, Metadata  } from 'libphonenumber-js';

export interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
}

let countryOptionsCache: CountryOption[] | null = null;
let callingCodeToCountriesCache: Map<string, CountryCode[]> | null = null;
let dialPrefixByCountryCache: Map<CountryCode, string> | null = null;

const resolveRegionNameResolver = () => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch (error) {
    return null;
  }
};

const buildCallingCodeMap = () => {
  if (callingCodeToCountriesCache) return callingCodeToCountriesCache;

  const map = new Map<string, CountryCode[]>();

  getCountries().forEach((code) => {
    const callingCode = getCountryCallingCode(code);
    map.set(callingCode, [...(map.get(callingCode) || []), code]);
  });

  callingCodeToCountriesCache = map;

  return map;
};

const getNationalLeadingDigits = (countryCode: CountryCode) => {
  try {
    const metadata = new Metadata();
    metadata.selectNumberingPlan(countryCode);
    const leadingDigits = metadata.numberingPlan?.leadingDigits();
    if (leadingDigits && /^\d+$/.test(leadingDigits)) return leadingDigits;
  } catch (error) {
    return '';
  }

  return '';
};

const buildDialPrefixByCountry = () => {
  if (dialPrefixByCountryCache) return dialPrefixByCountryCache;

  const callingCodes = buildCallingCodeMap();
  const map = new Map<CountryCode, string>();

  getCountries().forEach((code) => {
    const ituCallingCode = `${getCountryCallingCode(code)}`;
    const isSharedCallingCode = (callingCodes.get(ituCallingCode)?.length || 0) > 1;
    const leadingDigits = isSharedCallingCode ? getNationalLeadingDigits(code) : '';
    map.set(code, leadingDigits ? `${ituCallingCode}${leadingDigits}` : ituCallingCode);
  });

  dialPrefixByCountryCache = map;

  return map;
};

export default class PhoneNumberService {
  static resolveCountryCallingCodeByCountryCode = (countryCode: CountryCode) => getCountryCallingCode(countryCode)

  static getDialPrefixForCountry = (countryCode: CountryCode) => (
    buildDialPrefixByCountry().get(countryCode) || `${getCountryCallingCode(countryCode)}`
  )

  static getRealTimeNumberFormatter = (countryCode: CountryCode) => new AsYouType(countryCode || null)

  static isValid = (number: any, countryCode: CountryCode = 'GB') => !!(parsePhoneNumberFromString(number, countryCode)?.isValid())

  static getNumber = (number: any, countryCode: CountryCode = 'GB') => parsePhoneNumberFromString(number, countryCode)

  static getCountryOptions = (allowedCallingCodes?: Array<string | number>): CountryOption[] => {
    if (!countryOptionsCache) {
      const regionNames = resolveRegionNameResolver();

      countryOptionsCache = getCountries()
        .map(code => ({
          code,
          name: regionNames?.of(code) || code,
          callingCode: `+${PhoneNumberService.getDialPrefixForCountry(code)}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    return PhoneNumberService.filterCountryOptionsForPicker(countryOptionsCache, allowedCallingCodes);
  }

  static normalizeCallingCode = (callingCode: any) => `${callingCode || ''}`.replace(/^\+/, '').trim();

  static parseIsoCountryCode = (rawCode: any): CountryCode | undefined => {
    const asIso = `${rawCode || ''}`.replace(/^\+/, '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(asIso) && getCountries().includes(asIso as CountryCode)) {
      return asIso as CountryCode;
    }

    return undefined;
  }

  static flattenAllowedEntries = (allowedCallingCodes?: Array<string | number> | string | number) => {
    if (allowedCallingCodes == null || allowedCallingCodes === '') return [];

    const list = Array.isArray(allowedCallingCodes)
      ? allowedCallingCodes
      : typeof allowedCallingCodes === 'object'
        ? Object.values(allowedCallingCodes)
        : [allowedCallingCodes];

    return list
      .flatMap(entry => `${entry ?? ''}`.split(/[,\s]+/))
      .map(entry => entry.trim())
      .filter(entry => entry && entry !== '[object Object]');
  }

  static normalizeAllowedCountryCodes = (allowedCountryCodes?: Array<string | number>) => {
    const allowed = new Set<CountryCode>();

    PhoneNumberService.flattenAllowedEntries(allowedCountryCodes).forEach((entry) => {
      PhoneNumberService.resolveCountryCodesForAllowedEntry(entry).forEach(countryCode => {
        allowed.add(countryCode);
      });
    });

    return allowed;
  };

  /**
   * Picker list from getCountryOptions.
   * Calling codes `1` / `44` keep one row each (US / GB).
   * ISO lists keep every listed country (`GB` + `GG` stays two rows),
   * unless the list is the full shared group (GB/GG/JE/IM), which is treated as `44`.
   */
  static filterCountryOptionsForPicker = (options: CountryOption[], allowedCallingCodes?: Array<string | number>) => {
    const entries = PhoneNumberService.flattenAllowedEntries(allowedCallingCodes);
    if (!entries.length) return options;

    const pickerCountries = new Set<CountryCode>();
    const representatives: Record<string, CountryCode> = {
      '1': 'US',
      '44': 'GB',
    };
    const callingCodes = buildCallingCodeMap();
    const numericCallingCodes = new Set<string>();

    entries.forEach((entry) => {
      const isoCountry = PhoneNumberService.parseIsoCountryCode(entry);
      if (isoCountry) {
        pickerCountries.add(isoCountry);
        return;
      }

      const code = PhoneNumberService.normalizeCallingCode(entry);
      if (!code) return;

      numericCallingCodes.add(code);

      if (representatives[code]) {
        pickerCountries.add(representatives[code]);
        return;
      }

      const countriesForCode = callingCodes.get(code);
      if (countriesForCode?.length) {
        const mainCountry = countriesForCode.find(country => (
          PhoneNumberService.getDialPrefixForCountry(country) === code
        )) || countriesForCode[0];
        pickerCountries.add(mainCountry);
        return;
      }

      const asYouType = new AsYouType();
      asYouType.input(`+${code}`);
      if (asYouType.country) pickerCountries.add(asYouType.country);
    });

    Object.entries(representatives).forEach(([ituCode, representative]) => {
      const group = callingCodes.get(ituCode) || [];
      if (group.length < 2) return;

      const listedFromGroup = group.filter(country => pickerCountries.has(country));
      const hasCallingCodeEntry = numericCallingCodes.has(ituCode);
      const listedEntireGroup = listedFromGroup.length === group.length;

      if (!hasCallingCodeEntry && !listedEntireGroup) return;

      listedFromGroup.forEach(country => pickerCountries.delete(country));
      pickerCountries.add(representative);
    });

    if (!pickerCountries.size) return [];

    return options.filter(country => pickerCountries.has(country.code));
  }

  /**
   * Maps a tenant outbound entry to the country shown in the dialler picker.
   * ISO lists (`GB`, `US`, `CA`) keep the listed country.
   * Calling-code lists show one option per shared code: `1` → US, `44` → GB.
   * NANP values such as `1246` still resolve to that territory (Barbados).
   */
  static resolveCountryCodesForAllowedEntry = (rawCode: any): CountryCode[] => {
    const raw = `${rawCode || ''}`.trim();
    if (!raw) return [];

    const isoCountry = PhoneNumberService.parseIsoCountryCode(raw);
    if (isoCountry) return [isoCountry];

    const code = PhoneNumberService.normalizeCallingCode(raw);
    if (!code) return [];

    const sharedCallingCodeRepresentatives: Record<string, CountryCode> = {
      '1': 'US',
      '44': 'GB',
    };
    const representative = sharedCallingCodeRepresentatives[code];
    if (representative) return [representative];

    const asYouType = new AsYouType();
    asYouType.input(`+${code}`);
    if (asYouType.country) return [asYouType.country];

    return buildCallingCodeMap().get(code) || [];
  }

  static getAllowedCountryOptions = (allowedCallingCodes?: Array<string | number>): CountryOption[] => (
    PhoneNumberService.getCountryOptions(allowedCallingCodes)
  )

  /**
   * True when the number may be dialed for this tenant list.
   * ISO codes (`GB`, `US`) match that country only.
   * Calling codes (`1`, `44`) still allow every territory that shares the code,
   * even though the picker only shows US / GB. `1246` remains Barbados.
   */
  static isAllowedOutboundNumber = (number: any, allowedCallingCodes?: Array<string | number>, defaultCountry?: CountryCode) => {
    const entries = (allowedCallingCodes || [])
      .map(entry => `${entry || ''}`.trim())
      .filter(Boolean);

    if (!entries.length) return false;

    const parsed = parsePhoneNumberFromString(`${number || ''}`.trim(), defaultCountry);
    const country = parsed?.country;
    if (!country) return false;

    const allowedCountries = new Set<CountryCode>();
    const allowedItuCallingCodes = new Set<string>();
    const callingCodes = buildCallingCodeMap();

    entries.forEach((entry) => {
      const isoCountry = PhoneNumberService.parseIsoCountryCode(entry);
      if (isoCountry) {
        allowedCountries.add(isoCountry);
        return;
      }

      const code = PhoneNumberService.normalizeCallingCode(entry);
      if (!code) return;

      if (callingCodes.has(code)) {
        allowedItuCallingCodes.add(code);
        return;
      }

      PhoneNumberService.resolveCountryCodesForAllowedEntry(entry).forEach(countryCode => {
        allowedCountries.add(countryCode);
      });
    });

    if (allowedCountries.has(country)) return true;

    const numberCallingCode = parsed.countryCallingCode;
    return !!numberCallingCode && allowedItuCallingCodes.has(`${numberCallingCode}`);
  }

  /**
   * Resolves the leading country calling code of a number in international format.
   * Calling codes are prefix free, so the longest match is unambiguous.
   */
  static resolveCallingCodePrefix = (number: string) => {
    if (!number?.startsWith('+')) return '';

    const digits = number.slice(1).replace(/\D/g, '');
    const callingCodes = buildCallingCodeMap();

    for (let length = Math.min(3, digits.length); length > 0; length -= 1) {
      const candidate = digits.slice(0, length);
      if (callingCodes.has(candidate)) return candidate;
    }

    // The user may be part way through typing a calling code, in which case
    // the digits so far still belong to the calling code rather than the number.
    const isPartialCallingCode = digits.length <= 3
      && Array.from(callingCodes.keys()).some(code => code.startsWith(digits) && !!digits);

    return isPartialCallingCode ? digits : '';
  }

  static resolveCountryFromNumber = (number: any): CountryCode | undefined => {
    if (!number) return undefined;

    const parsedCountry = parsePhoneNumberFromString(number)?.country;
    if (parsedCountry) return parsedCountry;

    const callingCode = PhoneNumberService.resolveCallingCodePrefix(number);
    const countries = callingCode ? buildCallingCodeMap().get(callingCode) : undefined;

    // Calling codes such as +1 are shared, so only resolve when it maps to a single country.
    return countries?.length === 1 ? countries[0] : undefined;
  }

  static formatToE164 = (number: any, countryCode?: CountryCode) => {
    const currentNumber = `${number || ''}`.trim();
    if (!currentNumber) return '';

    const parsed = parsePhoneNumberFromString(currentNumber, countryCode);
    return parsed?.isValid() ? `${parsed.number}` : '';
  }

  /**
   * National significant number for the selected country, without a trunk prefix.
   * UK `01202158713` becomes `1202158713`; Italy keeps a significant leading `0`.
   */
  static resolveNationalNumber = (number: any, countryCode?: CountryCode) => {
    const currentNumber = `${number || ''}`.trim();
    if (!currentNumber) return '';

    if (currentNumber.startsWith('+')) {
      const parsed = parsePhoneNumberFromString(currentNumber);
      if (parsed?.nationalNumber) return `${parsed.nationalNumber}`;

      const currentCallingCode = PhoneNumberService.resolveCallingCodePrefix(currentNumber);
      return currentNumber.slice(currentCallingCode.length + 1).replace(/\D/g, '');
    }

    const parsed = parsePhoneNumberFromString(currentNumber, countryCode);
    if (parsed?.nationalNumber) return `${parsed.nationalNumber}`;

    return currentNumber.replace(/\D/g, '');
  }

  /**
   * True when the field is empty or only a calling code (`+1`, `+44`), with no
   * national digits. That should be replaced when the picker country changes.
   */
  static isCallingCodeOnly = (number: any) => {
    const currentNumber = `${number || ''}`.trim();
    if (!currentNumber) return true;
    if (!currentNumber.startsWith('+')) return false;

    const digits = currentNumber.slice(1).replace(/\D/g, '');
    if (!digits) return true;
    if (buildCallingCodeMap().has(digits)) return true;

    const asYouType = new AsYouType();
    asYouType.input(currentNumber);
    if (asYouType.country) {
      return PhoneNumberService.getDialPrefixForCountry(asYouType.country) === digits;
    }

    const currentCallingCode = PhoneNumberService.resolveCallingCodePrefix(currentNumber);
    const nationalDigits = currentNumber.slice(currentCallingCode.length + 1).replace(/\D/g, '');

    return !nationalDigits;
  }

  /**
   * Applies the selected country's calling code and formats to E.164 only when
   * libphonenumber can treat the input as a possible number for that country.
   * Incomplete or invalid input is left unchanged so a trunk `0` is not glued
   * onto `+CC` (e.g. US + `0123456789` must not become `+10123456789`).
   */
  static applyCountryCallingCode = (number: any, countryCode: CountryCode) => {
    const dialPrefix = `+${PhoneNumberService.getDialPrefixForCountry(countryCode)}`;
    const ituCallingCode = `+${getCountryCallingCode(countryCode)}`;
    const currentNumber = `${number || ''}`.trim();

    if (PhoneNumberService.isCallingCodeOnly(currentNumber)) return dialPrefix;

    if (currentNumber.startsWith('+')) {
      const parsedInternational = parsePhoneNumberFromString(currentNumber);

      if (parsedInternational?.country === countryCode && parsedInternational.isValid()) {
        return `${parsedInternational.number}`;
      }

      const nationalNumber = parsedInternational?.nationalNumber
        ? `${parsedInternational.nationalNumber}`
        : PhoneNumberService.resolveNationalNumber(currentNumber, countryCode);
      const rebuilt = parsePhoneNumberFromString(`${ituCallingCode}${nationalNumber}`, countryCode);

      return rebuilt?.isValid() ? `${rebuilt.number}` : currentNumber;
    }

    const parsedNational = parsePhoneNumberFromString(currentNumber, countryCode);
    return parsedNational?.isValid() ? `${parsedNational.number}` : currentNumber;
  }
}
